import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  toast,
} from "@eduviaos/ui-kit";
import { AlertCircle, GraduationCap, Loader2 } from "lucide-react";
import { useConvertEnquiry } from "@/hooks/useAdmissions";

/**
 * "Convert to admission" — promotes the enquiry into an Admission
 * record (real student in the system, gets an admission number, etc).
 *
 * Capability semantics (matches BE service.convert_enquiry):
 *   - When the funnel has `allow_direct_admission=true`, the BE will
 *     accept convert from any stage and auto-advance to terminal
 *     admitted as part of the same transaction.
 *   - When `allow_direct_admission=false` (the safe default), the
 *     enquiry MUST already be at the terminal-admitted stage; this
 *     keeps the funnel's audit trail honest. The BE returns a clear
 *     `INVALID_TRANSITION` with a message describing the requirement
 *     — we surface that verbatim below.
 *
 * Idempotent on the BE: a second click on an already-converted
 * enquiry just returns 200 with the existing Admission. We still
 * close the dialog optimistically — the cache invalidation pulls the
 * enquiry off the active list either way.
 */

interface EnquiryConvertDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  enquiryId: string;
  studentName?: string;
  /** Fired after a successful 200/201 with the new admission's id. */
  onConverted?: (admissionId: string) => void;
}

export function EnquiryConvertDialog({
  open,
  onOpenChange,
  enquiryId,
  studentName,
  onConverted,
}: EnquiryConvertDialogProps) {
  const convert = useConvertEnquiry(enquiryId);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const onConfirm = async () => {
    setError(null);
    try {
      const created = await convert.mutateAsync({});
      toast.success(
        studentName ? `Converted ${studentName} to admission.` : "Converted enquiry to admission.",
      );
      onOpenChange(false);
      onConverted?.(created.id);
    } catch (err) {
      const e = err as { message?: string; code?: string };
      // Map a couple of well-known BE codes to friendlier copy than
      // the raw envelope message; everything else falls back to the
      // server-supplied message.
      if (e?.code === "STAGE_NOT_TERMINAL_ADMITTED") {
        setError(
          "Your funnel doesn't have an 'admitted' stage configured yet. " +
            "Ask a Super Admin to update the admissions flow.",
        );
        return;
      }
      setError(e?.message ?? "Couldn't convert the enquiry. Please try again.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <span className="inline-flex size-8 items-center justify-center rounded-md bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
              <GraduationCap className="size-4" aria-hidden="true" />
            </span>
            Convert to admission
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {studentName ? (
              <>
                Promote <span className="text-foreground font-medium">{studentName}</span> from
                prospective to enrolled. They'll get an admission number, land in the Admissions
                register, and be available across the rest of the platform.
              </>
            ) : (
              "Promote this enquiry to enrolled. They'll get an admission number and be available across the rest of the platform."
            )}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <p
            role="alert"
            className="text-destructive border-destructive/30 bg-destructive/5 mt-2 flex items-start gap-1.5 rounded-md border px-3 py-2 text-xs"
          >
            <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </p>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={convert.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={convert.isPending}
            data-testid="convert-confirm"
            className="bg-emerald-600 text-white hover:bg-emerald-700"
          >
            {convert.isPending ? (
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            ) : (
              <GraduationCap className="size-4" aria-hidden="true" />
            )}
            Convert to admission
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
